// Crystal Globe data — Jellycat/plush companion system
// Source of truth for crystal color palettes, auras, comfort items, pins, worn items

export interface CrystalGuide {
  id: string;           // matches CRYSTAL_CHARACTERS id
  name: string;
  body: string;
  belly: string;
  cheek: string;
  eye: string;
  accent: string;
  bg: [string, string];
  /** Light-mode interior background gradient pair */
  bgLight: [string, string];
  shape: CrystalShape;
}

export type CrystalShape =
  | 'chunky' | 'round' | 'egg' | 'teardrop' | 'tall'
  | 'pair' | 'shard' | 'sphere' | 'oblong' | 'crescent';

export interface Aura {
  id: string;
  name: string;
  color: string;
  level: number;
}

export interface ComfortItem {
  id: string;
  name: string;
  level: number;
}

// ── Crystal Guides ──────────────────────────────────────────────────────

export const CRYSTAL_GUIDES: CrystalGuide[] = [
  { id: 'crystal-onyx',         name: 'Onyx',         body: '#222238', belly: '#2e2e48', cheek: '#3a3a58', eye: '#8b6fb0', accent: '#6b4c9a', bg: ['#0c0a14', '#12101e'], bgLight: ['#bcb0d8', '#a89ac8'], shape: 'chunky' },
  { id: 'crystal-rose-quartz',  name: 'Rose Quartz',  body: '#c07088', belly: '#d8a0b2', cheek: '#e8c0d0', eye: '#5a2838', accent: '#a85a70', bg: ['#140c10', '#1e1420'], bgLight: ['#ecbcce', '#e0a4bc'], shape: 'round' },
  { id: 'crystal-amethyst',     name: 'Amethyst',     body: '#5a4080', belly: '#7858a8', cheek: '#9878c0', eye: '#d0c0e8', accent: '#7b5ca0', bg: ['#0c0814', '#14101e'], bgLight: ['#c8b0e0', '#b89cd4'], shape: 'egg' },
  { id: 'crystal-citrine',      name: 'Citrine',      body: '#c89838', belly: '#e0b850', cheek: '#f0d880', eye: '#584020', accent: '#a88030', bg: ['#12100a', '#1e1a10'], bgLight: ['#ecd884', '#e0c870'], shape: 'teardrop' },
  { id: 'crystal-sapphire',     name: 'Sapphire',     body: '#285888', belly: '#3870a0', cheek: '#5090c0', eye: '#b0d8f8', accent: '#2860a0', bg: ['#080c14', '#101824'], bgLight: ['#a4c4e8', '#8cb4dc'], shape: 'tall' },
  { id: 'crystal-jade',         name: 'Jade',         body: '#408868', belly: '#58a880', cheek: '#80c8a0', eye: '#183828', accent: '#4a9070', bg: ['#080e0a', '#101e14'], bgLight: ['#a4d8c0', '#8cc8a8'], shape: 'pair' },
  { id: 'crystal-obsidian',     name: 'Obsidian',     body: '#181828', belly: '#242438', cheek: '#303048', eye: '#c0c0d8', accent: '#484868', bg: ['#060608', '#0e0e14'], bgLight: ['#bcb8c8', '#a8a4b8'], shape: 'shard' },
  { id: 'crystal-pearl',        name: 'Pearl',        body: '#c0b098', belly: '#d8ccb8', cheek: '#e8e0d0', eye: '#504838', accent: '#c8b8a0', bg: ['#0e0c0a', '#1a1816'], bgLight: ['#dccdb6', '#cebc9c'], shape: 'sphere' },
  { id: 'crystal-tiger-eye',    name: 'Tiger Eye',    body: '#906828', belly: '#b08040', cheek: '#c8a060', eye: '#281808', accent: '#987030', bg: ['#120e08', '#1e1810'], bgLight: ['#dcbb88', '#cca870'], shape: 'oblong' },
  { id: 'crystal-moonstone',    name: 'Moonstone',    body: '#8898b0', belly: '#a0b0c8', cheek: '#b8c8e0', eye: '#404858', accent: '#90a0b8', bg: ['#0c0e14', '#161a24'], bgLight: ['#bcc8dc', '#a4b4c8'], shape: 'crescent' },
];

export function getCrystalGuide(characterId: string): CrystalGuide | undefined {
  return CRYSTAL_GUIDES.find(g => g.id === characterId);
}

// ── Auras ───────────────────────────────────────────────────────────────

export const AURAS: Aura[] = [
  // Level 1: immediate needs
  { id: 'none',       name: 'None',       color: 'transparent', level: 1 },
  { id: 'breathing',  name: 'Breathing',  color: '#7090b0',     level: 1 },
  { id: 'pain',       name: 'Pain',       color: '#c04040',     level: 1 },
  { id: 'rest',       name: 'Rest',       color: '#9088b8',     level: 1 },
  // Level 2: building capacity
  { id: 'healing',    name: 'Healing',    color: '#c8a050',     level: 2 },
  { id: 'protection', name: 'Protection', color: '#d4a843',     level: 2 },
  { id: 'joy',        name: 'Joy',        color: '#e8c040',     level: 2 },
  // Level 3: deeper emotional range
  { id: 'grief',      name: 'Grief',      color: '#6878a0',     level: 3 },
  { id: 'spiritual',  name: 'Mindful',    color: '#c8b080',     level: 3 },
  { id: 'community',  name: 'Community',  color: '#60b898',     level: 3 },
  // Level 4: identity and expression
  { id: 'resistance', name: 'Rooted',     color: '#c07888',     level: 4 },
  { id: 'creative',   name: 'Creative',   color: '#c070b0',     level: 4 },
  // Level 5: full bond
  { id: 'careweave',  name: 'CareWeave',  color: '#8b6fb0',     level: 5 },
];

export function getUnlockedAuras(bondLevel: number): Aura[] {
  return AURAS.filter(a => a.level <= bondLevel);
}

// ── Comfort Items ───────────────────────────────────────────────────────

export const COMFORT_ITEMS: ComfortItem[] = [
  // Level 1: basics, comfort, survival
  { id: 'water',      name: 'Water Bottle',    level: 1 },
  { id: 'phone',      name: 'Phone',           level: 1 },
  { id: 'candle',     name: 'Candle',          level: 1 },
  { id: 'plushie',    name: 'Plushie',         level: 1 },
  // Level 2: coping tools
  { id: 'journal',    name: 'Journal',         level: 2 },
  { id: 'fidget',     name: 'Fidget Toy',      level: 2 },
  { id: 'controller', name: 'Controller',      level: 2 },
  { id: 'matcha',     name: 'Matcha',          level: 2 },
  // Level 3: expression and joy
  { id: 'boba',       name: 'Boba Tea',        level: 3 },
  { id: 'sketchbook', name: 'Sketchbook',      level: 3 },
  { id: 'camera',     name: 'Camera',          level: 3 },
  { id: 'book',       name: 'Manga / Book',    level: 3 },
  { id: 'cat',        name: 'Cat Companion',   level: 3 },
  // Level 4: resistance and creation
  { id: 'mic',        name: 'Spoken Word Mic', level: 4 },
  { id: 'palette',    name: 'Paint Palette',   level: 4 },
  { id: 'knitting',   name: 'Knitting',        level: 4 },
  { id: 'sign',       name: 'Protest Sign',    level: 4 },
  // Level 5: full bond
  { id: 'icedcoffee', name: 'Iced Coffee',     level: 5 },
  { id: 'basketball', name: 'Basketball',      level: 5 },
  { id: 'soccerball', name: 'Soccer Ball',     level: 5 },
];

export function getUnlockedItems(bondLevel: number): ComfortItem[] {
  return COMFORT_ITEMS.filter(i => i.level <= bondLevel);
}
